// Portable choices contain logical native voices, never deployment profile IDs.
export const CHARACTER_VOICES = Object.freeze([
  Object.freeze({ id: 'af_heart', name: 'Heart', provider: 'voicebox', engine: 'kokoro' }),
  Object.freeze({ id: 'bm_fable', name: 'Fable', provider: 'voicebox', engine: 'kokoro' }),
  Object.freeze({ id: 'af_bella', name: 'Bella', provider: 'voicebox', engine: 'kokoro' }),
  Object.freeze({ id: 'am_adam', name: 'Adam', provider: 'voicebox', engine: 'kokoro' }),
]);
const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
const object = value => value && typeof value === 'object' && !Array.isArray(value);
const unresolved = () => ({ provider: 'voicebox', nativeVoiceId: null, source: 'unresolved' });
export const DIRECTION_VOICE_ID = 'af_heart';
export const DEFAULT_DIALOGUE_VOICE_ID = 'bm_fable';

// The instruction channel owns Heart by default. Automatic legacy assignments
// that also landed on Heart resolve to the stable dialogue default at render
// time; deliberate manual Heart choices remain exact.
export function effectiveDialogueVoiceId(scenario, characterId) {
  if (!characterId) return DEFAULT_DIALOGUE_VOICE_ID;
  const choice = scenario?.characterVoices?.assignments?.[characterId];
  if (!choice?.nativeVoiceId) return null;
  return choice.source === 'automatic' && choice.nativeVoiceId === DIRECTION_VOICE_ID
    ? DEFAULT_DIALOGUE_VOICE_ID : choice.nativeVoiceId;
}

export function normalizeCharacterVoices(input, characters, ambiguousIds = new Set()) {
  if (input !== undefined && (!object(input) || input.version !== 1 || !object(input.assignments))) {
    throw new Error('Saved character voices have an unsupported or damaged format. Keep the original project and restore a valid backup.');
  }
  const saved = input?.assignments || {}, assignments = {}, counts = new Map(CHARACTER_VOICES.map(v => [v.id, 0]));
  // Validate even removed entries: a damaged save must not silently overwrite its source.
  for (const choice of Object.values(saved)) {
    if (!object(choice) || choice.provider !== 'voicebox' ||
        !['automatic', 'manual', 'unresolved'].includes(choice.source) ||
        (choice.source === 'unresolved' ? choice.nativeVoiceId !== null : typeof choice.nativeVoiceId !== 'string' || !/^[a-z][a-z0-9_]{1,63}$/.test(choice.nativeVoiceId))) {
      throw new Error('A saved character voice is invalid. The original project has not been overwritten.');
    }
  }
  for (const character of characters) {
    if (input && ambiguousIds.has(character.id)) assignments[character.id] = unresolved();
    else if (own(saved, character.id)) {
      const { provider, nativeVoiceId, source } = saved[character.id];
      assignments[character.id] = { provider, nativeVoiceId, source };
      if (counts.has(nativeVoiceId)) counts.set(nativeVoiceId, counts.get(nativeVoiceId) + 1);
    }
  }
  // Stable IDs determine new defaults, so a reorder never changes existing choices.
  for (const { id } of [...characters].sort((a, b) => a.id.localeCompare(b.id, 'en'))) {
    if (own(assignments, id)) continue;
    const voice = [...counts].sort((a, b) => a[1] - b[1] || CHARACTER_VOICES.findIndex(v => v.id === a[0]) - CHARACTER_VOICES.findIndex(v => v.id === b[0]))[0][0];
    assignments[id] = { provider: 'voicebox', nativeVoiceId: voice, source: 'automatic' };
    counts.set(voice, counts.get(voice) + 1);
  }
  const orderedAssignments = Object.fromEntries(Object.keys(assignments).sort((a, b) => a.localeCompare(b, 'en')).map(id => [id, assignments[id]]));
  return { version: 1, assignments: orderedAssignments };
}

export function setCharacterVoice(scenario, characterId, nativeVoiceId) {
  if (!scenario.characters.some(c => c.id === characterId) || !CHARACTER_VOICES.some(v => v.id === nativeVoiceId)) throw new Error('Choose an available character and native voice.');
  const voices = normalizeCharacterVoices(scenario.characterVoices, scenario.characters);
  return { ...scenario, characterVoices: { version: 1, assignments: { ...voices.assignments,
    [characterId]: { provider: 'voicebox', nativeVoiceId, source: 'manual' },
  } } };
}

// Preparation will add model revision/language/settings to this dependency.
// This contract deliberately ignores cue order, labels and display names.
export function characterLineDependencies(scenario) {
  return Object.fromEntries((scenario.cues || []).filter(c => c.type === 'dialogue' && c.characterId).map(c => {
    const voice = scenario.characterVoices?.assignments[c.characterId];
    return [c.id, JSON.stringify([c.dialogueText ?? c.text, c.characterId, voice?.provider ?? null, effectiveDialogueVoiceId(scenario, c.characterId)])];
  }));
}
export function changedCharacterLines(before, after) {
  const prior = characterLineDependencies(before), next = characterLineDependencies(after);
  return [...new Set([...Object.keys(prior), ...Object.keys(next)])].filter(id => prior[id] !== next[id]);
}
